import {
  BrowserStorageKeys,
  QueryStringParams,
} from '../../Common/Constants/QueryString_Keys.Constant';
import { isEmptyOrSpaces } from '../../Utilities/Helpers';
import { ClientSessionContext } from '../Services.Primary/SessionContext/ClientSessionContext';
import FeatureStorageService from './FeatureStorageService';
import { QueryStringParserService } from './QueryStringParserService';

interface FlightInteractionData {
  [key: string]: string;
}

export default class FlightingInteractionService extends FeatureStorageService {
  protected static instance: FlightingInteractionService;

  private clientSessionContext: ClientSessionContext;

  // Add back Environment Service to prevent query string based flight manipulation in Prod Env.
  /*private environmentService: EnvironmentService;*/

  public static getInstance = () => {
    if (!FlightingInteractionService.instance) {
      FlightingInteractionService.instance = new FlightingInteractionService();
    }
    return FlightingInteractionService.instance;
  };

  private constructor() {
    super();
    this.clientSessionContext = ClientSessionContext.getInstance();
  }

  /**
   * Process Query String and Enables the Flight.
   * So in order to enable Flight for Support UI :
   * setSupportFlight={flightName}:{status};{flightName1}:{status}
   * Here status will be either 0 or 1
   * This will automatically update the Flight for Panel as well by updating
   * its sessionStorage.
   */
  public processQueryStringFlights = () => {
    const queryParams = QueryStringParserService.getQueryParameters(window.location.search);

    if (queryParams) {
      if (queryParams[QueryStringParams.setSupportFlight]) {
        this.validateQueryStringAndSetFeature(
          BrowserStorageKeys.panelFlight,
          queryParams[QueryStringParams.setSupportFlight],
          this.modifyPanelAndSWFlightStorages
        );
      }
    }
  };

  /**
   * Specifies the status of the current Flight
   * @param flightName Name of the current Flight to check For
   * @param sessionContextStatus Shows whether the flight is applicable or not based
   * on the AppConfig.
   */
  public isFlightApplicable = (flightName: string, appConfigFlightStatus: boolean): boolean => {
    //[TODO]: Add it back when the Support Panel is GA.
    ///**
    // * For Prod we ignore the session Storage completely in all the calls.
    // */
    //if (this.environmentService.isProdEnvironment())
    //    return appConfigFlightStatus;

    const statusCheck = this.checkFlightStorage(flightName);

    if (statusCheck === null) return appConfigFlightStatus;

    return statusCheck;
  };

  /**
   * This gets the list of active Flights.
   * This is derived from the combination of Applicable flights from the App Config
   * and the ones that has been set by the queryString.
   */
  public getAllActiveFlights = async (): Promise<string[]> => {
    const applicableFlights = (await this.clientSessionContext.getContext())?.applicableFlights ?? [];
    const currentFlightList = [...applicableFlights];

    // Add it back when the Support Panel is GA.
    //if (this.environmentService.isProdEnvironment())
    //    return currentFlightList;

    const overrideFlightList = this.getFlightStorage();

    const flightList = Object.getOwnPropertyNames(overrideFlightList);
    if (flightList && Array.isArray(flightList)) {
      flightList.forEach(flightName => {
        if (!isEmptyOrSpaces(flightName)) {
          const flightStatus = this.checkFlightStorage(flightName);

          const indexOfFlightInAppConfigList = currentFlightList.indexOf(flightName);

          if (flightStatus !== null) {
            if (flightStatus && indexOfFlightInAppConfigList === -1) {
              currentFlightList.push(flightName);
            }

            if (!flightStatus && indexOfFlightInAppConfigList > -1) {
              currentFlightList.splice(indexOfFlightInAppConfigList, 1);
            }
          }
        }
      });
    }

    return currentFlightList;
  };

  /**
   * Modifies the Flight Storage for both the Panel and Support Workspace.
   * @param flightName Name of the flight to be added or modified
   * @param enable True asks to enable the flight and False disables the flight
   * @param flightKey Flight Key - This is based on whether the flight is for Support UI or panel.
   */
  private modifyPanelAndSWFlightStorages = (storageKey: string, flightName: string, enable: boolean) => {
    //Panel flight storage
    this.modifyFeatureStorage(storageKey, flightName, enable);
    //UI flight storage
    this.modifyFlightsStorage(flightName, enable);
  };

  private modifyFlightsStorage = (flightName: string, enable: boolean) => {
    const flightList = this.getFlightStorage();

    if (enable) {
      flightList[flightName] = '1';
    }

    if (!enable) {
      flightList[flightName] = '0';
    }

    this.browserStorage.setItem(BrowserStorageKeys.supportWorkspace, flightList);
  };

  /**
   *  Gets the list from the Storage maintained for Support UI.
   */
  private getFlightStorage = (): FlightInteractionData => {
    let flightList = {} as FlightInteractionData;
    const flights: FlightInteractionData = this.browserStorage.getItem(BrowserStorageKeys.supportWorkspace);

    if (flights && flights instanceof Object) {
      flightList = flights;
    }

    return flightList;
  };

  /**
   * Checks whether the given flight Exists in the given storage
   * @param flightName Name of the flight to check for
   * @returns boolean specifying whether the flight is Enabled or Not
   * and null if no information is available
   */
  private checkFlightStorage = (flightName: string): boolean | null => {
    const flightList = this.getFlightStorage();
    if (flightList[flightName]) {
      if (flightList[flightName] === '1') return true;

      if (flightList[flightName] === '0') return false;
    }

    return null;
  };
}
